import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Checkbox } from './components/ui/checkbox';
import { toast, Toaster } from 'sonner';
import { Lock, MapPin, Download, Search, Filter, LogOut, ArrowRight, ArrowLeft, Trash2, Plus, GripVertical, Settings, Clock, Home, Eye, FileSpreadsheet, MapPinned, BarChart3, FileText, Shield, List, AlertTriangle, Pencil } from 'lucide-react';
import AdminSidebar from './components/AdminSidebar';
import DevisPage from './components/DevisPage';
import VisiteTerrainPage from './components/VisiteTerrainPage';
import StatistiquesPage from './components/StatistiquesPage';
import CompteRenduPage from './components/CompteRenduPage';
import PermissionsPage from './components/PermissionsPage';
import PointageDetailModal from './components/PointageDetailModal';
import { getTheme } from './theme';
import { AdminPageHeader, AdminSection, adminPageStackClass } from './components/AdminChrome';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Normaliser l'URL API : éviter le double /api si déjà présent dans BACKEND_URL
const API = BACKEND_URL?.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

/** Affichage des heures de scan (liste + rapports) : heure légale France. */
const TIMEZONE_PARIS = { timeZone: 'Europe/Paris' };

/** Seuil stats GPS (m) : au-delà, précision considérée comme faible (> 1 km). */
const GPS_PRECISION_GOOD_MAX_M = 1000;

function isGpsPrecisionWeakMeters(precision) {
  if (precision == null || precision === '') return false;
  const n = Number(precision);
  return Number.isFinite(n) && n > GPS_PRECISION_GOOD_MAX_M;
}

/** Même fond / surfaces que l’admin (clair ou sombre selon localStorage darkMode). */
const usePublicPageTheme = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'darkMode') setIsDarkMode(e.newValue === 'true');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return getTheme(isDarkMode);
};

// Composant Canvas pour signature
const SignatureCanvas = ({ onSignatureChange, clearSignature, setClearSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#066183';
    ctx.lineWidth = 3;
    
    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    
    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    if (clearSignature) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setClearSignature(false);
    }
  }, [clearSignature, setClearSignature]);

  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getEventPos(e);
    setLastPosition(pos);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getEventPos(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPosition.x, lastPosition.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    setLastPosition(pos);
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    
    // Convert canvas to base64
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');
    const base64Data = dataURL.split(',')[1];
    onSignatureChange(base64Data);
  };

  return (
    <canvas
      ref={canvasRef}
      className="signature-canvas"
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
      style={{
        border: '2px solid #066183',
        borderRadius: '8px',
        cursor: 'crosshair',
        touchAction: 'none'
      }}
    />
  );
};

// Page d'accueil avec choix Arrivée/Départ
const HomePage = () => {
  const navigate = useNavigate();
  const theme = usePublicPageTheme();

  return (
    <div
      className="min-h-screen w-full min-w-0"
      style={{ backgroundColor: theme.background }}
    >
      <div className="header-gradient p-4 sm:p-6 w-full">
        <div className="max-w-md mx-auto w-full px-1 sm:px-0">
          <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-2">
            Horizon Solution
          </h1>
          <p className="text-white/90 text-center text-sm">
            Suivi de prestation
          </p>
        </div>
      </div>

      <div className="w-full max-w-md mx-auto px-4 sm:px-6 py-6 -mt-4">
        <Card
          className="shadow-lg rounded-xl border"
          style={{ borderColor: theme.border, backgroundColor: theme.surface }}
        >
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-xl font-semibold mb-2" style={{ color: theme.text }}>
                  Choisissez votre scan
                </h2>
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  Sélectionnez le type de scan à effectuer
                </p>
              </div>
              
              {/* Bouton Arrivée */}
              <Button
                onClick={() => navigate('/pointage/arrivee')}
                className="w-full h-20 text-xl font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 flex items-center justify-center gap-3"
                data-testid="arrivee-btn"
              >
                <ArrowRight className="h-8 w-8" />
                ARRIVÉE
              </Button>
              
              {/* Bouton Départ */}
              <Button
                onClick={() => navigate('/pointage/depart')}
                className="w-full h-20 text-xl font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 flex items-center justify-center gap-3"
                data-testid="depart-btn"
              >
                <ArrowLeft className="h-8 w-8" />
                DÉPART
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Access */}
      <div className="fixed bottom-6 right-6" style={{zIndex: 9999}}>
        <Button
          onClick={() => navigate('/admin')}
          className="rounded-full w-14 h-14 bg-primary hover:bg-primary/90 shadow-lg"
          data-testid="admin-access-btn"
          style={{zIndex: 9999, position: 'relative'}}
        >
          <Lock className="h-6 w-6 text-white" />
        </Button>
      </div>
    </div>
  );
};

// Page de formulaire pointage (commune aux deux types)
const PointagePage = () => {
  const navigate = useNavigate();
  const typePointage = window.location.pathname.includes('arrivee') ? 'Arrivee' : 'Depart';
  
  const [formData, setFormData] = useState({ site: '', prenom: '', nom: '' });
  const [sites, setSites] = useState([]);
  const [siteSearch, setSiteSearch] = useState('');
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const [signature, setSignature] = useState('');
  const [clearSignature, setClearSignature] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    // Charger les sites
    const loadSites = async () => {
      try {
        const response = await axios.get(`${API}/sites`);
        setSites(response.data);
      } catch (error) {
        console.error('Erreur chargement sites:', error);
      }
    };
    loadSites();
  }, []);

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.site-selector')) {
        setIsSiteDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const theme = usePublicPageTheme();

  // Filtrer les sites selon la recherche
  const filteredSites = sites.filter(site => 
    site.nom.toLowerCase().includes(siteSearch.toLowerCase())
  );

  const handleSiteSelect = (siteName) => {
    setFormData(prev => ({ ...prev, site: siteName }));
    setSiteSearch('');
    setIsSiteDropdownOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Géolocalisation non supportée'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            precision: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.site || !formData.prenom || !formData.nom) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (!signature) {
      toast.error('Veuillez signer avant d\'envoyer');
      return;
    }

    setIsSubmitting(true);
    setLocationError('');

    try {
      // Get location
      const locationData = await getLocation();
      setLocation(locationData);

      // Submit data
      const response = await axios.post(`${API}/pointage`, {
        site: formData.site,
        prenom: formData.prenom,
        nom: formData.nom,
        type_pointage: typePointage,
        signature: signature,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        precision: locationData.precision,
        device_id: navigator.userAgent
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({ site: '', prenom: '', nom: '' });
        setSiteSearch('');
        setSignature('');
        setClearSignature(true);
        setLocation(null);
        
        // Redirect to home after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      if (error.message.includes('User denied')) {
        setLocationError('Veuillez autoriser la géolocalisation pour continuer le scan.');
        toast.error('Géolocalisation requise');
      } else {
        console.error('Erreur:', error);
        toast.error('Erreur lors de l\'envoi du scan');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearForm = () => {
    setFormData({ site: '', prenom: '', nom: '' });
    setSiteSearch('');
    setSignature('');
    setClearSignature(true);
  };

  const typeLabel = typePointage === 'Arrivee' ? 'Arrivée' : 'Départ';
  const typeColor = typePointage === 'Arrivee' ? 'bg-green-600' : 'bg-red-600';

  return (
    <div
      className="min-h-screen w-full min-w-0"
      style={{ backgroundColor: theme.background }}
    >
      <div className="header-gradient p-4 sm:p-6 w-full">
        <div className="max-w-md mx-auto w-full flex items-center gap-2 sm:gap-3 px-1 sm:px-0">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:bg-white/10 shrink-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0 text-center">
            <h1 className="text-lg sm:text-2xl font-bold text-white mb-1 truncate">
              Scan {typeLabel === 'Arrivée' ? 'arrivée' : 'de départ'}
            </h1>
            <p className="text-white/90 text-sm">
              Horizon Solution
            </p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-md mx-auto px-4 sm:px-6 py-6 -mt-4">
        <Card
          className="shadow-lg rounded-xl border"
          style={{ borderColor: theme.border, backgroundColor: theme.surface }}
        >
          <CardHeader>
            <div className={`${typeColor} text-white text-center py-3 rounded-lg mb-4`}>
              <CardTitle className="text-lg font-semibold">{typeLabel}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Site - Sélecteur amélioré avec recherche */}
              <div className="space-y-2">
                <Label htmlFor="site" className="text-lg font-medium flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-500" />
                  Site de travail *
                </Label>
                <div className="site-selector relative">
                  {/* Bouton de sélection */}
                  <button
                    type="button"
                    onClick={() => setIsSiteDropdownOpen(!isSiteDropdownOpen)}
                    className={`flex items-center justify-between w-full h-14 px-4 rounded-xl border-2 transition-all bg-white ${
                      formData.site 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid="site-select"
                  >
                    <span className={`text-lg ${formData.site ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                      {formData.site || 'Sélectionnez votre site...'}
                    </span>
                    <svg 
                      className={`h-5 w-5 text-gray-400 transition-transform ${isSiteDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  {isSiteDropdownOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                      {/* Barre de recherche */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Rechercher un site..."
                            value={siteSearch}
                            onChange={(e) => setSiteSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-base"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Liste des sites */}
                      <div className="max-h-64 overflow-y-auto">
                        {filteredSites.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            Aucun site trouvé
                          </div>
                        ) : (
                          filteredSites.map((site) => (
                            <button
                              key={site.id}
                              type="button"
                              onClick={() => handleSiteSelect(site.nom)}
                              className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 ${
                                formData.site === site.nom ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                formData.site === site.nom 
                                  ? 'bg-blue-500 text-white' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {site.nom.charAt(0)}
                              </div>
                              <span className={`text-base ${formData.site === site.nom ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>
                                {site.nom}
                              </span>
                              {formData.site === site.nom && (
                                <svg className="ml-auto h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          ))
                        )}
                      </div>

                      {/* Compteur */}
                      <div className="p-2 border-t border-gray-100 bg-gray-50 text-center">
                        <span className="text-xs text-gray-500">
                          {filteredSites.length} site{filteredSites.length > 1 ? 's' : ''} disponible{filteredSites.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Prénom */}
              <div className="space-y-2">
                <Label htmlFor="prenom" className="text-lg font-medium">
                  Prénom *
                </Label>
                <Input
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  placeholder="Votre prénom"
                  className="text-lg p-4 h-12"
                  data-testid="prenom-input"
                />
              </div>

              {/* Nom */}
              <div className="space-y-2">
                <Label htmlFor="nom" className="text-lg font-medium">
                  Nom *
                </Label>
                <Input
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  placeholder="Votre nom"
                  className="text-lg p-4 h-12"
                  data-testid="nom-input"
                />
              </div>

              {/* Signature */}
              <div className="space-y-2">
                <Label className="text-lg font-medium">
                  Signature *
                </Label>
                <p className="text-sm text-gray-600 mb-2">
                  Signez avec votre doigt dans la zone ci-dessous
                </p>
                <div className="signature-container" style={{ height: '150px' }}>
                  <SignatureCanvas
                    onSignatureChange={setSignature}
                    clearSignature={clearSignature}
                    setClearSignature={setClearSignature}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setClearSignature(true)}
                  className="w-full mt-2"
                  data-testid="clear-signature-btn"
                >
                  Effacer la signature
                </Button>
              </div>

              {/* Location Error */}
              {locationError && (
                <Alert className="border-red-200 bg-red-50">
                  <MapPin className="h-4 w-4 text-red-500" />
                  <AlertDescription className="text-red-700">
                    {locationError}
                  </AlertDescription>
                </Alert>
              )}

              {/* Location Success */}
              {location && (
                <Alert className="border-green-200 bg-green-50">
                  <MapPin className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-700">
                    Position GPS obtenue (précision: {Math.round(location.precision)}m)
                  </AlertDescription>
                </Alert>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearForm}
                  className="flex-1 h-12 text-lg"
                  data-testid="clear-form-btn"
                >
                  Effacer
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 h-12 text-lg ${typeColor} hover:opacity-90 text-white`}
                  data-testid="submit-pointage-btn"
                >
                  {isSubmitting ? 'Envoi...' : 'Envoyer'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Composant pour gérer les sites
const SitesConfiguration = ({ token }) => {
  const [sites, setSites] = useState([]);
  const [newSite, setNewSite] = useState('');
  const [loading, setLoading] = useState(false);
  const [draggedSite, setDraggedSite] = useState(null);

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`);
      setSites(response.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des sites');
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  const addSite = async () => {
    if (!newSite.trim()) {
      toast.error('Veuillez saisir un nom de site');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/sites`, 
        { nom: newSite.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewSite('');
      await loadSites();
      toast.success('Site ajouté avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'ajout du site');
    } finally {
      setLoading(false);
    }
  };

  const deleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le site "${siteName}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/sites/${siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await loadSites();
      toast.success('Site supprimé avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression du site');
    }
  };

  const updateSiteOrder = async (siteId, newOrder) => {
    try {
      await axios.put(`${API}/admin/sites/${siteId}`,
        { ordre: newOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await loadSites();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la mise à jour de l\'ordre');
    }
  };

  const moveSite = (index, direction) => {
    const newOrder = direction === 'up' ? sites[index].ordre - 1 : sites[index].ordre + 1;
    updateSiteOrder(sites[index].id, newOrder);
  };

  const handleDragStart = (e, site) => {
    setDraggedSite(site);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetSite) => {
    e.preventDefault();
    
    if (!draggedSite || draggedSite.id === targetSite.id) {
      setDraggedSite(null);
      return;
    }

    // Réorganiser les sites localement
    const draggedIndex = sites.findIndex(s => s.id === draggedSite.id);
    const targetIndex = sites.findIndex(s => s.id === targetSite.id);
    
    const newSites = [...sites];
    // Retirer le site déplacé
    newSites.splice(draggedIndex, 1);
    // L'insérer à la nouvelle position
    newSites.splice(targetIndex, 0, draggedSite);
    
    // Mettre à jour les ordres
    const updatedSites = newSites.map((site, index) => ({
      ...site,
      ordre: index + 1
    }));
    
    setSites(updatedSites);
    
    // Sauvegarder le nouvel ordre
    try {
      await Promise.all(
        updatedSites.map(site => 
          axios.put(`${API}/admin/sites/${site.id}`,
            { ordre: site.ordre },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success('Ordre des sites mis à jour');
      await loadSites();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la mise à jour de l\'ordre');
      await loadSites(); // Recharger en cas d'erreur
    }
    
    setDraggedSite(null);
  };

  return (
    <div className="space-y-6">
      {/* Ajouter un nouveau site */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Ajouter un nouveau site
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Nom du nouveau site"
              value={newSite}
              onChange={(e) => setNewSite(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addSite()}
              data-testid="new-site-input"
            />
            <Button 
              onClick={addSite} 
              disabled={loading || !newSite.trim()}
              data-testid="add-site-btn"
            >
              Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des sites */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Sites configurés ({sites.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sites.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun site configuré</p>
          ) : (
            <div className="space-y-2">
              {sites.map((site, index) => (
                <div 
                  key={site.id} 
                  className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg transition-all duration-200 ${
                    draggedSite?.id === site.id ? 'opacity-50 scale-95' : 'hover:bg-gray-100'
                  }`}
                  data-testid={`site-item-${site.id}`}
                  draggable="true"
                  onDragStart={(e) => handleDragStart(e, site)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, site)}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                    <span className="font-medium">{site.nom}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveSite(index, 'up')}
                      disabled={index === 0}
                      data-testid={`move-up-${site.id}`}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveSite(index, 'down')}
                      disabled={index === sites.length - 1}
                      data-testid={`move-down-${site.id}`}
                    >
                      ↓
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteSite(site.id, site.nom)}
                      data-testid={`delete-site-${site.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// Page d'administration avec Sidebar
const emptyManualScanForm = () => ({
  site: '',
  prenom: '',
  nom: '',
  typePointage: 'Arrivee',
  timestamp: '',
  admin_comment: '',
  location_address: '',
  latitude: '',
  longitude: '',
});

const emptyEditLocationForm = () => ({
  typePointage: 'Arrivee',
  location_address: '',
  latitude: '',
  longitude: '',
  precision: '',
});

const AdminPageWithSidebar = ({ token, handleLogout, currentUser, access }) => {
  const [pointages, setPointages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalScansCount, setTotalScansCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPointages, setSelectedPointages] = useState([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectedPointage, setSelectedPointage] = useState(null); // Pour le modal de détail
  const [scanSites, setScanSites] = useState([]);
  const [manualScanOpen, setManualScanOpen] = useState(false);
  const [manualScanForm, setManualScanForm] = useState(() => emptyManualScanForm());
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editLocationId, setEditLocationId] = useState(null);
  const [editLocationForm, setEditLocationForm] = useState(() => emptyEditLocationForm());
  const [editLocationSubmitting, setEditLocationSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('scans');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Gestion du responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Gestion du mode sombre
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    // Sauvegarde la préférence dans localStorage
    localStorage.setItem('darkMode', (!isDarkMode).toString());
  };

  // Chargement de la préférence thème au démarrage
  useEffect(() => {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'true');
    }
  }, []);

  const theme = getTheme(isDarkMode);

  const navigationItems = useMemo(() => {
    const all = [
      { id: 'scans', label: 'Suivis', icon: Clock, perm: 'suivis.access' },
      { id: 'visite-terrain', label: 'Visite terrain', icon: MapPinned, perm: 'visite.access' },
      { id: 'statistiques', label: 'Statistiques', icon: BarChart3, perm: 'stats.access' },
      { id: 'compte-rendu', label: 'Compte rendu', icon: FileText, perm: 'compte_rendu.access' },
      { id: 'devis', label: 'Devis', icon: FileSpreadsheet, perm: 'devis.access' },
      { id: 'permissions', label: 'Permissions', icon: Shield, perm: 'permissions.access' },
    ];
    if (!access) return all;
    return all.filter(
      (item) =>
        access.is_super_admin || (access.permissions || []).includes(item.perm)
    );
  }, [access]);

  useEffect(() => {
    if (!access) return;
    const allowed = new Set(navigationItems.map((i) => i.id));
    if (!allowed.has(activeTab)) {
      setActiveTab(navigationItems[0]?.id || 'scans');
    }
  }, [access, activeTab, navigationItems]);

  const activeNavLabel = useMemo(() => {
    const item = navigationItems.find((i) => i.id === activeTab);
    return item?.label || 'Administration';
  }, [navigationItems, activeTab]);

  const canWriteScans = useMemo(
    () => !!(access?.is_super_admin || (access?.permissions || []).includes('suivis.write')),
    [access]
  );

  useEffect(() => {
    if (activeTab !== 'scans' || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setScanSites(
            list
              .map((s) => ({
                id: String(s.id ?? ''),
                nom: (s.nom || s.name || '').trim(),
              }))
              .filter((s) => s.nom)
          );
        }
      } catch {
        if (!cancelled) setScanSites([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, token]);

  const [filters, setFilters] = useState({
    site: '',
    employe: '',
    typePointage: '',
    precisionFilter: '',
    dateFrom: '',
    dateTo: ''
  });

  // Additional state variables from original AdminPage
  const navigate = useNavigate();

  const appendScanFiltersToParams = (params) => {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (key === 'dateFrom') params.append('date_from', value);
        else if (key === 'dateTo') params.append('date_to', value);
        else if (key === 'typePointage') params.append('type_pointage', value);
        else if (key === 'precisionFilter' && value === 'gt_1km') {
          params.append('precision_filter', 'gt_1km');
        } else if (key !== 'precisionFilter') params.append(key, value);
      }
    });
  };

  const loadPointages = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      appendScanFiltersToParams(params);

      const response = await axios.get(`${API}/admin/pointages?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPointages(response.data.pointages);
      setTotalScansCount(
        typeof response.data.total === 'number' ? response.data.total : 0
      );
      setTotalPages(response.data.total_pages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      appendScanFiltersToParams(params);

      const response = await axios.get(`${API}/admin/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        format === 'pdf' ? 'rapport_suivi.pdf' : `pointages.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const deletePointage = async (pointageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce scan ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/pointages/${pointageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Scan supprimé avec succès');
      await loadPointages(currentPage);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteSelectedPointages = async () => {
    if (selectedPointages.length === 0) {
      toast.error('Aucun scan sélectionné');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedPointages.length} scan(s) ?`)) {
      return;
    }

    try {
      await axios.post(`${API}/admin/pointages/delete-multiple`, 
        { pointage_ids: selectedPointages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedPointages.length} scan(s) supprimé(s) avec succès`);
      setSelectedPointages([]);
      await loadPointages(currentPage);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression multiple');
    }
  };

  const togglePointageSelection = (pointageId) => {
    setSelectedPointages(prev => {
      if (prev.includes(pointageId)) {
        return prev.filter(id => id !== pointageId);
      } else {
        return [...prev, pointageId];
      }
    });
  };

// Fonction de changement de mot de passe supprimée

  const allFilteredScansSelected =
    totalScansCount > 0 && selectedPointages.length === totalScansCount;

  const scansHeaderCheckboxState =
    totalScansCount === 0
      ? false
      : allFilteredScansSelected
        ? true
        : selectedPointages.length > 0
          ? 'indeterminate'
          : false;

  const fetchAllFilteredPointageIds = async () => {
    const params = new URLSearchParams();
    appendScanFiltersToParams(params);
    const response = await axios.get(`${API}/admin/pointages/ids?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.ids || [];
  };

  const handleToggleSelectAllScans = async () => {
    if (totalScansCount === 0 || selectAllLoading || loading) return;
    if (allFilteredScansSelected) {
      setSelectedPointages([]);
      return;
    }
    setSelectAllLoading(true);
    try {
      const ids = await fetchAllFilteredPointageIds();
      setSelectedPointages(ids);
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        toast.error('Impossible de sélectionner tous les scans (toutes pages).');
      }
    } finally {
      setSelectAllLoading(false);
    }
  };

  const openEditLocationForPointage = (p) => {
    setSelectedPointage(null);
    setEditLocationId(p.id);
    const tp = p.type_pointage === 'Depart' ? 'Depart' : 'Arrivee';
    setEditLocationForm({
      typePointage: tp,
      location_address: p.location_address || '',
      latitude: p.latitude != null && !Number.isNaN(Number(p.latitude)) ? String(p.latitude) : '',
      longitude: p.longitude != null && !Number.isNaN(Number(p.longitude)) ? String(p.longitude) : '',
      precision: p.precision != null && !Number.isNaN(Number(p.precision)) ? String(p.precision) : '',
    });
    setEditLocationOpen(true);
  };

  const submitManualScan = async () => {
    const f = manualScanForm;
    if (!f.site.trim() || !f.prenom.trim() || !f.nom.trim()) {
      toast.error('Site, prénom et nom sont obligatoires');
      return;
    }
    if (!f.admin_comment.trim()) {
      toast.error('Le commentaire est obligatoire pour un ajout manuel');
      return;
    }
    const lt = f.latitude.trim();
    const lg = f.longitude.trim();
    if ((lt && !lg) || (!lt && lg)) {
      toast.error('Renseigner la latitude et la longitude ensemble, ou laisser les deux vides');
      return;
    }
    let latitude = null;
    let longitude = null;
    if (lt && lg) {
      latitude = parseFloat(lt.replace(',', '.'));
      longitude = parseFloat(lg.replace(',', '.'));
      if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
        toast.error('Latitude ou longitude invalide');
        return;
      }
    }
    let timestamp = null;
    if (f.timestamp) {
      const d = new Date(f.timestamp);
      if (Number.isNaN(d.getTime())) {
        toast.error('Date/heure invalide');
        return;
      }
      timestamp = d.toISOString();
    }
    setManualSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/pointages/manual`,
        {
          site: f.site.trim(),
          prenom: f.prenom.trim(),
          nom: f.nom.trim(),
          type_pointage: f.typePointage,
          admin_comment: f.admin_comment.trim(),
          timestamp,
          latitude,
          longitude,
          location_address: f.location_address.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Scan enregistré');
      setManualScanOpen(false);
      setManualScanForm(emptyManualScanForm());
      await loadPointages(1);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Impossible d’enregistrer le scan');
      if (error.response?.status === 401) handleLogout();
    } finally {
      setManualSubmitting(false);
    }
  };

  const submitEditLocation = async () => {
    if (!editLocationId) return;
    const f = editLocationForm;
    const lt = f.latitude.trim();
    const lg = f.longitude.trim();
    if ((lt && !lg) || (!lt && lg)) {
      toast.error('Latitude et longitude : remplir les deux champs ou les deux vides (pour effacer les coordonnées)');
      return;
    }
    const body = {
      type_pointage: f.typePointage === 'Depart' ? 'Depart' : 'Arrivee',
      location_address: f.location_address.trim() || null,
    };
    if (lt && lg) {
      body.latitude = parseFloat(lt.replace(',', '.'));
      body.longitude = parseFloat(lg.replace(',', '.'));
    } else {
      body.latitude = null;
      body.longitude = null;
    }
    const pr = f.precision.trim();
    if (pr) {
      const p = parseFloat(pr.replace(',', '.'));
      if (Number.isNaN(p)) {
        toast.error('Précision GPS invalide');
        return;
      }
      body.precision = p;
    } else {
      body.precision = null;
    }
    setEditLocationSubmitting(true);
    try {
      await axios.patch(`${API}/admin/pointages/${editLocationId}`, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      toast.success('Scan mis à jour');
      setEditLocationOpen(false);
      setEditLocationId(null);
      await loadPointages(currentPage);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Impossible de mettre à jour le scan');
      if (error.response?.status === 401) handleLogout();
    } finally {
      setEditLocationSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'scans') {
      setSelectedPointages([]);
      loadPointages();
    }
  }, [filters, activeTab]);

  return (
    <div 
      className={`min-h-screen flex ${isDarkMode ? 'dark' : ''}`}
      style={{ backgroundColor: theme.background }}
    >
      {/* Overlay mobile */}
      {isMobile && isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AdminSidebar 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
        currentUser={currentUser}
        handleLogout={handleLogout}
        navigate={navigate}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        isMobile={isMobile}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        navigationItems={navigationItems}
      />

      {/* Menu Mobile Burger Button */}
      {isMobile && (
        <Button
          className="fixed top-4 left-4 z-60 bg-[#066183] hover:bg-[#054d6b] text-white shadow-lg rounded-xl"
          size="sm"
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        >
          <div className="w-4 h-4 flex flex-col justify-between">
            <div className="w-full h-0.5 bg-white rounded-full"></div>
            <div className="w-full h-0.5 bg-white rounded-full"></div>
            <div className="w-full h-0.5 bg-white rounded-full"></div>
          </div>
        </Button>
      )}

      {/* Contenu Principal */}
      <div className={`flex-1 flex flex-col min-w-0 ${isMobile ? 'ml-0' : ''}`}>
        {/* Header Mobile */}
        {isMobile && (
          <div 
            className="p-4 pl-16 border-b backdrop-blur-sm"
            style={{ 
              backgroundColor: theme.surface,
              borderColor: theme.border,
              color: theme.text 
            }}
          >
            <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>
              Espace administrateur
            </p>
            <h1 className="text-xl font-bold tracking-tight">{activeNavLabel}</h1>
          </div>
        )}

        {/* Zone de contenu avec scroll */}
        <div 
          className="flex-1 overflow-y-auto antialiased"
          style={{ backgroundColor: theme.background }}
        >
          <div className="w-full min-w-0 max-w-none mx-0 px-3 py-4 sm:px-4 sm:py-5 md:px-5 lg:px-6 lg:py-6 xl:px-8">
            {/* Contenu des onglets */}
            {activeTab === 'devis' && (
              <DevisPage
                token={token}
                apiBase={`${API}/admin/chiffrage`}
                theme={theme}
                access={access}
                onUnauthorized={handleLogout}
              />
            )}

            {activeTab === 'visite-terrain' && (
              <VisiteTerrainPage
                token={token}
                apiBase={API}
                theme={theme}
                onUnauthorized={handleLogout}
              />
            )}

            {activeTab === 'statistiques' && (
              <StatistiquesPage
                token={token}
                apiBase={API}
                theme={theme}
                onUnauthorized={handleLogout}
              />
            )}

            {activeTab === 'compte-rendu' && (
              <CompteRenduPage
                token={token}
                apiBase={API}
                theme={theme}
                onUnauthorized={handleLogout}
              />
            )}

            {activeTab === 'permissions' && (
              <PermissionsPage
                token={token}
                apiBase={API}
                theme={theme}
                access={access}
                onUnauthorized={handleLogout}
              />
            )}

            {activeTab === 'scans' && (
              <div className={`${adminPageStackClass} sm:space-y-6`}>
                <AdminPageHeader
                  theme={theme}
                  icon={Clock}
                  title="Suivis"
                  subtitle="Consultez, filtrez et exportez les scans d’arrivée et de départ. Les données affichées respectent votre périmètre d’accès."
                />
                <AdminSection
                  theme={theme}
                  icon={Filter}
                  title="Filtres et recherche"
                  description="Affinez la liste par site, employé, type, précision GPS et période."
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
                    <div>
                      <label 
                        htmlFor="filter-site" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.text }}
                      >
                        Site
                      </label>
                      <Input
                        id="filter-site"
                        placeholder="Rechercher par site"
                        value={filters.site}
                        onChange={(e) => setFilters(prev => ({...prev, site: e.target.value}))}
                        data-testid="filter-site-input"
                        className="rounded-xl border"
                        style={{ 
                          backgroundColor: theme.background,
                          borderColor: theme.border,
                          color: theme.text
                        }}
                      />
                    </div>
                    <div>
                      <label 
                        htmlFor="filter-employe" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.text }}
                      >
                        Employé
                      </label>
                      <Input
                        id="filter-employe"
                        placeholder="Prénom ou nom"
                        value={filters.employe}
                        onChange={(e) => setFilters(prev => ({...prev, employe: e.target.value}))}
                        data-testid="filter-employe-input"
                        className="rounded-xl border"
                        style={{ 
                          backgroundColor: theme.background,
                          borderColor: theme.border,
                          color: theme.text
                        }}
                      />
                    </div>
                    <div>
                      <label 
                        htmlFor="filter-type" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.text }}
                      >
                        Type
                      </label>
                      <select
                        className="flex h-10 w-full rounded-xl border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                        value={filters.typePointage}
                        onChange={(e) => setFilters(prev => ({...prev, typePointage: e.target.value}))}
                        data-testid="filter-type-select"
                        style={{ 
                          backgroundColor: theme.background,
                          borderColor: theme.border,
                          color: theme.text
                        }}
                      >
                        <option value="">Tous</option>
                        <option value="Arrivee">Arrivée</option>
                        <option value="Depart">Départ</option>
                      </select>
                    </div>
                    <div>
                      <label 
                        htmlFor="filter-precision-gps" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.text }}
                      >
                        Précision GPS
                      </label>
                      <select
                        id="filter-precision-gps"
                        className="flex h-10 w-full rounded-xl border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                        value={filters.precisionFilter}
                        onChange={(e) => setFilters(prev => ({ ...prev, precisionFilter: e.target.value }))}
                        data-testid="filter-precision-select"
                        style={{ 
                          backgroundColor: theme.background,
                          borderColor: theme.border,
                          color: theme.text
                        }}
                      >
                        <option value="">Tous</option>
                        <option value="gt_1km">Plus de 1 km</option>
                      </select>
                    </div>
                    <div>
                      <label 
                        htmlFor="filter-date-from" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.text }}
                      >
                        Date de début
                      </label>
                      <Input
                        id="filter-date-from"
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => setFilters(prev => ({...prev, dateFrom: e.target.value}))}
                        data-testid="filter-date-from-input"
                        className="rounded-xl border"
                        style={{ 
                          backgroundColor: theme.background,
                          borderColor: theme.border,
                          color: theme.text
                        }}
                      />
                    </div>
                    <div>
                      <label 
                        htmlFor="filter-date-to" 
                        className="block text-sm font-medium mb-2"
                        style={{ color: theme.text }}
                      >
                        Date de fin
                      </label>
                      <Input
                        id="filter-date-to"
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => setFilters(prev => ({...prev, dateTo: e.target.value}))}
                        data-testid="filter-date-to-input"
                        className="rounded-xl border"
                        style={{ 
                          backgroundColor: theme.background,
                          borderColor: theme.border,
                          color: theme.text
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6 flex-wrap">
                    <Button 
                      onClick={() => loadPointages(1)} 
                      className="text-white font-medium px-6 py-2 rounded-xl shadow-sm"
                      style={{ backgroundColor: theme.accent }}
                      data-testid="apply-filters-btn"
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Appliquer les filtres
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setFilters({
                          site: '',
                          employe: '',
                          typePointage: '',
                          precisionFilter: '',
                          dateFrom: '',
                          dateTo: ''
                        });
                      }}
                      data-testid="reset-filters-btn"
                      className="rounded-xl font-medium"
                      style={{ 
                        borderColor: theme.border,
                        color: theme.textSecondary,
                        backgroundColor: 'transparent'
                      }}
                    >
                      Réinitialiser
                    </Button>
                    {canWriteScans ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setManualScanForm(emptyManualScanForm());
                          setManualScanOpen(true);
                        }}
                        className="rounded-xl font-medium"
                        style={{
                          borderColor: theme.border,
                          color: theme.textSecondary,
                          backgroundColor: 'transparent',
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Ajout manuel
                      </Button>
                    ) : null}
                    <Button 
                      variant="outline" 
                      onClick={() => exportData('csv')}
                      data-testid="export-csv-btn"
                      className="rounded-xl font-medium"
                      style={{ 
                        borderColor: theme.border,
                        color: theme.textSecondary,
                        backgroundColor: 'transparent'
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => exportData('pdf')}
                      data-testid="export-pdf-btn"
                      className="rounded-xl font-medium"
                      style={{ 
                        borderColor: theme.border,
                        color: theme.textSecondary,
                        backgroundColor: 'transparent'
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export PDF
                    </Button>
                    {selectedPointages.length > 0 && (
                      <Button 
                        variant="destructive" 
                        onClick={deleteSelectedPointages}
                        data-testid="delete-selected-btn"
                        className="rounded-xl font-medium"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer ({selectedPointages.length})
                      </Button>
                    )}
                  </div>
                </AdminSection>

                <AdminSection
                  theme={theme}
                  icon={List}
                  title="Liste des scans"
                  description={`${totalScansCount} scan${totalScansCount !== 1 ? 's' : ''} trouvé${totalScansCount !== 1 ? 's' : ''}`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div
                        className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full"
                        style={{ borderColor: theme.accent, borderTopColor: 'transparent' }}
                      />
                      <span className="ml-3" style={{ color: theme.textSecondary }}>Chargement des scans...</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Version Desktop - Tableau */}
                      <div className="hidden lg:block w-full min-w-0 overflow-x-auto">
                        <table className="w-full min-w-[720px]">
                          <thead>
                            <tr 
                              className="border-b"
                              style={{ borderColor: theme.border }}
                            >
                              <th className="text-left p-3 w-10">
                                <Checkbox 
                                  checked={scansHeaderCheckboxState}
                                  disabled={loading || selectAllLoading || totalScansCount === 0}
                                  onCheckedChange={() => {
                                    void handleToggleSelectAllScans();
                                  }}
                                />
                              </th>
                              <th className="text-left p-3 font-semibold text-sm" style={{ color: theme.text }}>
                                Employé
                              </th>
                              <th className="text-left p-3 font-semibold text-sm" style={{ color: theme.text }}>
                                Site
                              </th>
                              <th className="text-left p-3 font-semibold text-sm" style={{ color: theme.text }}>
                                Type
                              </th>
                              <th className="text-left p-3 font-semibold text-sm" style={{ color: theme.text }}>
                                Date & Heure
                              </th>
                              <th className="text-center p-3 font-semibold text-sm" style={{ color: theme.text }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pointages.map((pointage) => (
                              <tr 
                                key={pointage.id} 
                                className="border-b transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                style={{ borderColor: theme.border }}
                              >
                                <td className="p-3">
                                  <Checkbox 
                                    checked={selectedPointages.includes(pointage.id)}
                                    onCheckedChange={() => togglePointageSelection(pointage.id)}
                                  />
                                </td>
                                <td className="p-3">
                                  <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: theme.text }}>
                                    {isGpsPrecisionWeakMeters(pointage.precision) ? (
                                      <span
                                        className="inline-flex shrink-0"
                                        title="Précision GPS supérieure à 1 km"
                                        aria-label="Précision GPS supérieure à 1 km"
                                      >
                                        <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
                                      </span>
                                    ) : null}
                                    <span>
                                      {pointage.prenom} {pointage.nom}
                                    </span>
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div className="flex flex-col gap-1 min-w-0">
                                    <span className="text-sm" style={{ color: theme.textSecondary }}>
                                      {pointage.site}
                                    </span>
                                    {pointage.source === 'admin_manual' ? (
                                      <span
                                        className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 self-start"
                                        style={{ backgroundColor: theme.accentSoft, color: theme.accent }}
                                      >
                                        Ajout Manuel Administrateur
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                                <td className="p-3">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                                    pointage.type_pointage === 'Arrivee' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {pointage.type_pointage === 'Arrivee' ? '↓ Arrivée' : '↑ Départ'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div>
                                    <span className="font-medium text-sm" style={{ color: theme.text }}>
                                      {new Date(pointage.timestamp).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        ...TIMEZONE_PARIS,
                                      })}
                                    </span>
                                    <span className="block text-xs" style={{ color: theme.textSecondary }}>
                                      {new Date(pointage.timestamp).toLocaleDateString('fr-FR', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short',
                                        ...TIMEZONE_PARIS,
                                      })}
                                    </span>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <div className="flex items-center justify-center gap-2">
                                    {canWriteScans ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openEditLocationForPointage(pointage)}
                                        className="rounded-lg h-8 w-8 p-0"
                                        style={{ borderColor: theme.border, color: theme.accent }}
                                        title="Modifier le scan (type, adresse, coordonnées)"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    ) : null}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setSelectedPointage(pointage)}
                                      className="rounded-lg h-8 w-8 p-0"
                                      style={{ 
                                        borderColor: theme.border,
                                        color: (pointage.latitude != null && pointage.longitude != null) ? '#3b82f6' : theme.textSecondary
                                      }}
                                      title={(pointage.latitude != null && pointage.longitude != null) ? "Voir localisation" : "Voir détails"}
                                    >
                                      {(pointage.latitude != null && pointage.longitude != null) ? <MapPin className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deletePointage(pointage.id)}
                                      className="rounded-lg h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Version Mobile/Tablet - Cartes */}
                      <div className="lg:hidden space-y-3">
                        {/* Sélection globale mobile */}
                        <div 
                          className="flex items-center gap-3 p-3 rounded-xl border"
                          style={{ backgroundColor: theme.background, borderColor: theme.border }}
                        >
                          <Checkbox 
                            checked={scansHeaderCheckboxState}
                            disabled={loading || selectAllLoading || totalScansCount === 0}
                            onCheckedChange={() => {
                              void handleToggleSelectAllScans();
                            }}
                          />
                          <span className="text-sm" style={{ color: theme.textSecondary }}>
                            {selectAllLoading
                              ? 'Chargement…'
                              : `Tout sélectionner (${totalScansCount} résultat${totalScansCount !== 1 ? 's' : ''}, toutes pages)`}
                          </span>
                        </div>

                        {/* Liste des cartes */}
                        {pointages.map((pointage) => (
                          <div 
                            key={pointage.id}
                            className="rounded-xl border p-4 transition-all hover:shadow-md"
                            style={{ 
                              backgroundColor: theme.surface,
                              borderColor: selectedPointages.includes(pointage.id) ? '#3b82f6' : theme.border,
                              borderWidth: selectedPointages.includes(pointage.id) ? '2px' : '1px'
                            }}
                          >
                            {/* En-tête carte */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Checkbox 
                                  checked={selectedPointages.includes(pointage.id)}
                                  onCheckedChange={() => togglePointageSelection(pointage.id)}
                                />
                                <div>
                                  <p className="font-semibold flex items-center gap-1.5 flex-wrap" style={{ color: theme.text }}>
                                    {isGpsPrecisionWeakMeters(pointage.precision) ? (
                                      <span
                                        className="inline-flex shrink-0"
                                        title="Précision GPS supérieure à 1 km"
                                        aria-label="Précision GPS supérieure à 1 km"
                                      >
                                        <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
                                      </span>
                                    ) : null}
                                    <span>
                                      {pointage.prenom} {pointage.nom}
                                    </span>
                                  </p>
                                  <p className="text-sm" style={{ color: theme.textSecondary }}>
                                    {pointage.site}
                                  </p>
                                  {pointage.source === 'admin_manual' ? (
                                    <span
                                      className="inline-block text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 mt-1"
                                      style={{ backgroundColor: theme.accentSoft, color: theme.accent }}
                                    >
                                      Ajout Manuel Administrateur
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                                pointage.type_pointage === 'Arrivee' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {pointage.type_pointage === 'Arrivee' ? '↓ Arrivée' : '↑ Départ'}
                              </span>
                            </div>

                            {/* Informations */}
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Clock className="h-4 w-4" style={{ color: theme.textSecondary }} />
                                <span className="text-sm font-medium" style={{ color: theme.text }}>
                                  {new Date(pointage.timestamp).toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    ...TIMEZONE_PARIS,
                                  })}
                                </span>
                                <span className="text-sm" style={{ color: theme.textSecondary }}>
                                  • {new Date(pointage.timestamp).toLocaleDateString('fr-FR', {
                                    day: 'numeric',
                                    month: 'short',
                                    ...TIMEZONE_PARIS,
                                  })}
                                </span>
                                {pointage.latitude != null && pointage.longitude != null ? (
                                  <MapPin className="h-4 w-4 text-blue-500 ml-2" />
                                ) : null}
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-2 flex-wrap justify-end">
                                {canWriteScans ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEditLocationForPointage(pointage)}
                                    className="rounded-lg h-9 px-3"
                                    style={{ borderColor: theme.border, color: theme.accent }}
                                  >
                                    <Pencil className="h-4 w-4 mr-1" />
                                    <span className="text-xs">Modifier</span>
                                  </Button>
                                ) : null}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedPointage(pointage)}
                                  className="rounded-lg h-9 px-3"
                                  style={{ borderColor: theme.border }}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  <span className="text-xs">Détails</span>
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deletePointage(pointage.id)}
                                  className="rounded-lg h-9 w-9 p-0 text-red-500 hover:bg-red-50 border-red-200"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div 
                          className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 sm:mt-6 p-3 rounded-xl w-full min-w-0"
                          style={{ backgroundColor: theme.background }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadPointages(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="rounded-lg h-9 px-4"
                            style={{ borderColor: theme.border, color: theme.textSecondary }}
                          >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Précédent</span>
                          </Button>
                          <div className="flex items-center gap-2">
                            <span 
                              className="text-sm font-medium px-3 py-1 rounded-lg"
                              style={{ backgroundColor: theme.accent, color: 'white' }}
                            >
                              {currentPage}
                            </span>
                            <span className="text-sm" style={{ color: theme.textSecondary }}>
                              / {totalPages}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadPointages(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="rounded-lg h-9 px-4"
                            style={{ borderColor: theme.border, color: theme.textSecondary }}
                          >
                            <span className="hidden sm:inline">Suivant</span>
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </AdminSection>

                <Dialog open={manualScanOpen} onOpenChange={setManualScanOpen}>
                  <DialogContent
                    className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl"
                    style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
                  >
                    <DialogHeader>
                      <DialogTitle style={{ color: theme.text }}>Ajout manuel de scan</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                      <p className="text-xs" style={{ color: theme.textSecondary }}>
                        Enregistrement sans géolocalisation : le commentaire est obligatoire. Le scan sera étiqueté « Ajout
                        manuel administrateur ».
                      </p>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Site (entreprise)
                        </Label>
                        <select
                          className="flex h-10 w-full rounded-xl border px-3 py-2 text-sm"
                          style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}
                          value={manualScanForm.site}
                          onChange={(e) => setManualScanForm((prev) => ({ ...prev, site: e.target.value }))}
                        >
                          <option value="">— Choisir —</option>
                          {scanSites.map((s) => (
                            <option key={s.id} value={s.nom}>
                              {s.nom}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs" style={{ color: theme.textSecondary }}>
                            Prénom
                          </Label>
                          <Input
                            value={manualScanForm.prenom}
                            onChange={(e) => setManualScanForm((prev) => ({ ...prev, prenom: e.target.value }))}
                            className="rounded-xl"
                            style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs" style={{ color: theme.textSecondary }}>
                            Nom
                          </Label>
                          <Input
                            value={manualScanForm.nom}
                            onChange={(e) => setManualScanForm((prev) => ({ ...prev, nom: e.target.value }))}
                            className="rounded-xl"
                            style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Type
                        </Label>
                        <select
                          className="flex h-10 w-full rounded-xl border px-3 py-2 text-sm"
                          style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}
                          value={manualScanForm.typePointage}
                          onChange={(e) => setManualScanForm((prev) => ({ ...prev, typePointage: e.target.value }))}
                        >
                          <option value="Arrivee">Arrivée</option>
                          <option value="Depart">Départ</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Date et heure (optionnel — défaut : maintenant)
                        </Label>
                        <Input
                          type="datetime-local"
                          value={manualScanForm.timestamp}
                          onChange={(e) => setManualScanForm((prev) => ({ ...prev, timestamp: e.target.value }))}
                          className="rounded-xl"
                          style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Commentaire <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          rows={3}
                          value={manualScanForm.admin_comment}
                          onChange={(e) => setManualScanForm((prev) => ({ ...prev, admin_comment: e.target.value }))}
                          placeholder="Motif de l’ajustement, contexte…"
                          className="rounded-xl text-sm"
                          style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Adresse / lieu exact (optionnel)
                        </Label>
                        <Textarea
                          rows={2}
                          value={manualScanForm.location_address}
                          onChange={(e) => setManualScanForm((prev) => ({ ...prev, location_address: e.target.value }))}
                          placeholder="Ex. 12 rue de la République, 69002 Lyon"
                          className="rounded-xl text-sm"
                          style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs" style={{ color: theme.textSecondary }}>
                            Latitude (optionnel)
                          </Label>
                          <Input
                            value={manualScanForm.latitude}
                            onChange={(e) => setManualScanForm((prev) => ({ ...prev, latitude: e.target.value }))}
                            className="rounded-xl tabular-nums"
                            style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                            placeholder="ex. 45.767"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs" style={{ color: theme.textSecondary }}>
                            Longitude (optionnel)
                          </Label>
                          <Input
                            value={manualScanForm.longitude}
                            onChange={(e) => setManualScanForm((prev) => ({ ...prev, longitude: e.target.value }))}
                            className="rounded-xl tabular-nums"
                            style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                            placeholder="ex. 4.834"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-xl"
                          style={{ borderColor: theme.border }}
                          onClick={() => setManualScanOpen(false)}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="button"
                          className="rounded-xl text-white"
                          style={{ backgroundColor: theme.accent }}
                          disabled={manualSubmitting}
                          onClick={() => void submitManualScan()}
                        >
                          {manualSubmitting ? 'Enregistrement…' : 'Enregistrer'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={editLocationOpen} onOpenChange={setEditLocationOpen}>
                  <DialogContent
                    className="max-w-lg rounded-2xl"
                    style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
                  >
                    <DialogHeader>
                      <DialogTitle style={{ color: theme.text }}>Modifier le scan</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                      <p className="text-xs" style={{ color: theme.textSecondary }}>
                        Corriger le type (arrivée / départ) en cas d’erreur, et le lieu : adresse affichée sur la fiche
                        et/ou coordonnées GPS. Laisser latitude et longitude vides pour supprimer la position sur la
                        carte.
                      </p>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Type de scan
                        </Label>
                        <select
                          className="flex h-10 w-full rounded-xl border px-3 py-2 text-sm"
                          style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}
                          value={editLocationForm.typePointage}
                          onChange={(e) =>
                            setEditLocationForm((prev) => ({ ...prev, typePointage: e.target.value }))
                          }
                        >
                          <option value="Arrivee">Arrivée</option>
                          <option value="Depart">Départ</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Adresse exacte
                        </Label>
                        <Textarea
                          rows={3}
                          value={editLocationForm.location_address}
                          onChange={(e) =>
                            setEditLocationForm((prev) => ({ ...prev, location_address: e.target.value }))
                          }
                          className="rounded-xl text-sm"
                          style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs" style={{ color: theme.textSecondary }}>
                            Latitude
                          </Label>
                          <Input
                            value={editLocationForm.latitude}
                            onChange={(e) => setEditLocationForm((prev) => ({ ...prev, latitude: e.target.value }))}
                            className="rounded-xl tabular-nums"
                            style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs" style={{ color: theme.textSecondary }}>
                            Longitude
                          </Label>
                          <Input
                            value={editLocationForm.longitude}
                            onChange={(e) => setEditLocationForm((prev) => ({ ...prev, longitude: e.target.value }))}
                            className="rounded-xl tabular-nums"
                            style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs" style={{ color: theme.textSecondary }}>
                          Précision GPS (mètres, optionnel)
                        </Label>
                        <Input
                          value={editLocationForm.precision}
                          onChange={(e) => setEditLocationForm((prev) => ({ ...prev, precision: e.target.value }))}
                          className="rounded-xl tabular-nums"
                          style={{ borderColor: theme.border, backgroundColor: theme.background, color: theme.text }}
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-xl"
                          style={{ borderColor: theme.border }}
                          onClick={() => {
                            setEditLocationOpen(false);
                            setEditLocationId(null);
                          }}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="button"
                          className="rounded-xl text-white"
                          style={{ backgroundColor: theme.accent }}
                          disabled={editLocationSubmitting}
                          onClick={() => void submitEditLocation()}
                        >
                          {editLocationSubmitting ? 'Enregistrement…' : 'Mettre à jour'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <PointageDetailModal
                  pointage={selectedPointage}
                  isOpen={!!selectedPointage}
                  onClose={() => setSelectedPointage(null)}
                  theme={theme}
                  canEditLocation={canWriteScans}
                  onEditLocation={(p) => openEditLocationForPointage(p)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component for AdminPage that manages token
const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [currentUser, setCurrentUser] = useState(null);
  const [access, setAccess] = useState(null);

  const fetchSession = async (authToken) => {
    try {
      const meRes = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setAccess(meRes.data);
      const adminsRes = await axios.get(`${API}/admin/administrators`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const uname = meRes.data?.username;
      const row = (adminsRes.data || []).find((a) => a.username === uname);
      setCurrentUser(row || { username: uname, display_name: uname });
    } catch (error) {
      console.error('Erreur récupération session:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSession(token);
    }
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setCurrentUser(null);
    setAccess(null);
    navigate('/');
  };

  // If no token, show login form
  if (!token) {
    return <AdminLoginForm setToken={setToken} navigate={navigate} />;
  }

  // If token exists, show the sidebar admin interface
  return (
    <AdminPageWithSidebar
      token={token}
      handleLogout={handleLogout}
      currentUser={currentUser}
      access={access}
    />
  );
};

// Login form component
const AdminLoginForm = ({ setToken, navigate }) => {
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      const { token: newToken } = response.data;
      localStorage.setItem('admin_token', newToken);
      setToken(newToken);
      toast.success('Connexion réussie');
    } catch (error) {
      toast.error('Identifiants incorrects');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-slate-100 via-slate-50 to-cyan-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <Card className="w-full max-w-md rounded-2xl border-0 shadow-xl shadow-slate-900/10 dark:shadow-black/40 overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[#066183] via-cyan-500 to-[#066183]" />
        <CardHeader className="space-y-1 pt-8 pb-2">
          <CardTitle className="text-center text-2xl font-bold tracking-tight text-[#066183] dark:text-cyan-400">
            Administration
          </CardTitle>
          <p className="text-center text-sm text-muted-foreground">Horizon Solution — espace réservé</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Identifiant</Label>
              <Input
                id="username"
                value={loginForm.username}
                onChange={(e) => setLoginForm(prev => ({...prev, username: e.target.value}))}
                data-testid="admin-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({...prev, password: e.target.value}))}
                data-testid="admin-password-input"
              />
            </div>
            <Button type="submit" className="w-full bg-primary" data-testid="admin-login-btn">
              Se connecter
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="w-full"
              data-testid="back-to-home-btn"
            >
              Retour à l'accueil
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// App principale
function App() {
  return (
    <div className="App w-full min-h-screen min-w-0">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/pointage/arrivee" element={<PointagePage />} />
          <Route path="/pointage/depart" element={<PointagePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;